import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type AppNotification } from "@/lib/api";

export const NOTIFICATIONS_KEY = ["notifications"] as const;
/** How often the list is read: "live" within half a minute, and on returning to the window. */
export const POLL_MS = 30_000;

/** The signed-in reader's notifications, shared by the bell and the toaster. */
export function useNotifications(enabled = true) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api.notifications.list(1, 30),
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: 1,
  });
  const items: AppNotification[] = query.data?.items ?? [];
  const unread = items.filter((n) => !n.isRead).length;

  const setRead = useCallback(
    (ids: readonly string[] | "all") =>
      qc.setQueryData<{ items: AppNotification[]; totalCount: number }>(NOTIFICATIONS_KEY, (d) =>
        d ? { ...d, items: d.items.map((n) => (ids === "all" || ids.includes(n.id) ? { ...n, isRead: true } : n)) } : d,
      ),
    [qc],
  );
  const markRead = useCallback(
    async (ids: readonly string[]) => {
      setRead(ids);
      await Promise.all(ids.map((id) => api.notifications.markRead(id).catch(() => {})));
    },
    [setRead],
  );
  const markAllRead = useCallback(async () => {
    setRead("all");
    await api.notifications.markAllRead().catch(() => {});
  }, [setRead]);

  return { items, unread, loading: query.isLoading, markRead, markAllRead };
}
